import { useEffect, useState } from 'react';
import { getData } from '../../../DataAccessLayer';
import { ats, cct } from '../../../DataAccessLayer/services';
import Loader from '../../common/Loader';
import { Box } from '@mui/material';
import CourseInfo from './CourseInfo';
import Meetings from './Meetings';
import { Time } from '../../common/MomentTime';
import DeleteMeetingModal from '../../MyCourses/CCT/Body/Zoom/Meetings/DeleteMeetingModal';
import {
  addAlternativeHost,
  addCodecs,
  createCalendarEntry,
  deleteCalendarEntry,
  deleteMeeting,
  enableGuestSpeaker,
  enableJoinBeforeHost,
  isAddCodecsAllowed,
  removeCodecs,
} from '../utils';
import { toast } from 'react-toastify';
import { startMeeting } from '../../MyCourses/CCT/utils';
import { useSelector } from 'react-redux';
import AddAlternativeHosts from '../../MyCourses/CCT/Body/Zoom/Meetings/AddAlternativeHosts';
import PortalDelete from '../../common/PortalDelete';
import AddCodecsModal from '../Courses/AddCodecsModal';
import RemoveCodecsModal from '../Courses/RemoveCodecsModal';
import Codecs from './Codecs';
import ActionDialogs from '../Courses/ActionDialogs';
import Header from '../../MyCourses/CCT/Header';
import { CourseActions } from './CourseActions';
import { WithSmallScreen } from '../../common/WithSmallScreen';
import PortalAlert from '../../common/PortalAlert';
import ApiErrorMessage from '../../common/ApiErrorMessage';

export const CourseData = WithSmallScreen(({ id, crn, termCode, ...props }) => {
  const impersonation = useSelector((state) => state.impersonationReducer.impersonation);
  const trueUser = useSelector((state) => state.AWSReducer.user);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState({});
  const [course, setCourse] = useState();
  const [codecs, setCodecs] = useState({});
  const [pastMeetings, setPastMeetings] = useState([]);
  const [futureMeetings, setFutureMeetings] = useState([]);
  const [isInProgress, setIsInProgress] = useState(false);
  const [isDeleteMeetingsModalOpen, setIsDeleteMeetingsModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState({});
  const [isAddAltHostDisplay, setIsAddAltHostDisplay] = useState(false);
  const [alternativeHosts, setAlternativeHosts] = useState([]);
  const [isDeleteCalendarEntryModalOpen, setIsDeleteCalendarEntryModalOpen] = useState(false);
  const [isAddCodecsModalOpen, setIsAddCodecsModalOpen] = useState(false);
  const [isRemoveCodecsModalOpen, setIsRemoveCodecsModalOpen] = useState(false);
  const [isDeleteCodecModalOpen, setIsDeleteCodecModalOpen] = useState(false);
  const [selectedCodec, setSelectedCodec] = useState();
  const [courseActionMenu, setCourseActionMenu] = useState();

  useEffect(() => {
    loadPage();
  }, []);

  const loadPage = async () => {
    try {
      toggleIsLoading(true);
      toggleError();
      const [course] = await Promise.all([loadCourseDetails(), loadCodecs()]);
      await loadCourseMeetings(course);
    } catch (err) {
      console.log(err);
    } finally {
      toggleIsLoading(false);
    }
  };

  const loadCourseDetails = async () => {
    return getData(
      ats.courseDetails({
        crn,
        termCode,
      }),
      true,
    )
      .then((courseDetails) => {
        setCourse(courseDetails);
        return courseDetails;
      })
      .catch((err) => {
        console.log(err);
        toggleError({ courseDetails: true });
      });
  };

  const loadCodecs = async () => {
    return getData(ats.codecs, true)
      .then((codecs) => {
        setCodecs(
          codecs.reduce((codecsMap, codec) => {
            codecsMap[codec.ID] = codec;
            return codecsMap;
          }, {}),
        );
        return codecs;
      })
      .catch((err) => {
        console.log(err);
        toggleError({ codecs: true });
      });
  };

  const loadCourseMeetings = async (courseDetails) => {
    courseDetails = courseDetails ?? course;
    if (courseDetails) {
      return getData(
        cct({
          crn: courseDetails.CRN,
          termCode: courseDetails.TERM_CODE,
        }),
        true,
      )
        .then((courseMeetings) => {
          const { past, future } = getPastFutureMeetings(courseMeetings?.meetings ?? []);
          setPastMeetings(past);
          setFutureMeetings(future);
        })
        .catch((err) => {
          console.log(err);
          if (err?.data?.message !== 'No meetings found') {
            toggleError({ meetings: true });
          }
        });
    }
  };

  const getPastFutureMeetings = (meetings) => {
    return meetings.reduce(
      (pastFutureMeetings, meeting) => {
        if (
          meeting.start_url &&
          Time.University(meeting.start_time)
            .add(meeting.duration, 'minutes')
            .diff(Time.University(), 'minutes') < 0
        ) {
          pastFutureMeetings.past.push(meeting);
        } else {
          pastFutureMeetings.future.push(meeting);
        }
        return pastFutureMeetings;
      },
      { past: [], future: [] },
    );
  };

  const toggleIsInProgress = () => {
    setIsInProgress((prev) => !prev);
  };

  const toggleIsLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const toggleError = (err) => {
    setError((prev) => (err ? { ...prev, ...err } : {}));
  };

  const toggleIsDeleteMeetingsModalOpen = () => {
    setIsDeleteMeetingsModalOpen((prev) => !prev);
  };

  const onDelete = (meeting) => {
    setSelectedMeeting(meeting);
    toggleIsDeleteMeetingsModalOpen();
  };

  const onDeleteMeeting = async () => {
    try {
      const meetingId = selectedMeeting.id;
      toggleIsInProgress();
      const result = await deleteMeeting({
        crn,
        termCode,
        meetingId,
      });
      if (!result) toast.error(`Unable to delete meeting with ID ${selectedMeeting.id}.`);
      else {
        toast.success(`Meeting with ID ${selectedMeeting.id} deleted!`);

        await loadCourseMeetings();
      }
      toggleIsDeleteMeetingsModalOpen();
      setSelectedMeeting({});
    } catch (err) {
      console.log(err);
      toast.error(`Unable to delete meeting with ID ${selectedMeeting.id}.`);
    } finally {
      toggleIsInProgress();
    }
  };

  const onStart = (meeting) => {
    startMeeting(getUserEmail(trueUser, impersonation), meeting, course);
  };

  const onEnableGuestSpeaker = async (meeting) => {
    try {
      toggleIsInProgress();
      const result = await enableGuestSpeaker({
        crn: course.CRN,
        termCode: course.TERM_CODE,
        meetingId: meeting.id,
      });

      if (!result) toast.error(`Unable to enable guest speaker for meeting with ID ${meeting.id}.`);
      else {
        toast.success(`Enabled guest speaker for meeting with ID ${meeting.id}.`);
        await loadCourseMeetings();
      }
    } catch (err) {
      toast.error(`Unable to enable guest speaker for meeting with ID ${meeting.id}.`);
      console.log(err);
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsAddAltHostDisplay = () => {
    setIsAddAltHostDisplay((prev) => !prev);
  };

  const onAddAltHost = (meeting) => {
    setSelectedMeeting(meeting);
    toggleIsAddAltHostDisplay();
  };

  const onAddAlternativeHostConfirm = async (email) => {
    const isEditMode = selectedMeeting?.settings?.alternative_hosts?.length > 0;
    try {
      toggleIsInProgress();

      await addAlternativeHost({
        crn: course.CRN,
        termCode: course.TERM_CODE,
        meeting: selectedMeeting,
        email,
        course,
      });

      toast.success(
        !isEditMode
          ? `Added alternative host(s) to meeting with ID ${selectedMeeting.id}.`
          : `Updated the alternative host(s) for meeting with ID ${selectedMeeting.id}.`,
      );
      await loadCourseMeetings();
      resetAltHostFlags();
    } catch (err) {
      toast.error(
        !isEditMode
          ? `Unable to add alternative host(s) to meeting with ID ${selectedMeeting.id}`
          : `Unable to update alternative host(s) for meeting with ID ${selectedMeeting.id}`,
      );
      console.log(err);
    } finally {
      toggleIsInProgress();
    }
  };

  const resetAltHostFlags = () => {
    toggleIsAddAltHostDisplay();
    setSelectedMeeting({});
    setAlternativeHosts([]);
  };

  const onEnableJoinBeforeHost = async (meeting) => {
    try {
      toggleIsInProgress();
      const result = await enableJoinBeforeHost({
        crn: course.CRN,
        termCode: course.TERM_CODE,
        meetingId: meeting.id,
        joinBeforeHost: !meeting.settings.join_before_host,
      });

      if (!result)
        toast.error(`Unable to enable join before host for the meeting with ID ${meeting.id}.`);
      else {
        toast.success(`Enabled join before host for the meeting with ID ${meeting.id}.`);
        await loadCourseMeetings();
      }
    } catch (err) {
      toast.error(`Unable to enable join before host for the meeting with ID ${meeting.id}.`);
      console.log(err);
    } finally {
      toggleIsInProgress();
    }
  };

  const onCopyMeetingInvitation = async (meeting) => {
    try {
      toggleIsInProgress();
      await navigator.clipboard.writeText(meeting.join_url);
      toast.success(`Copied the meeting link of meeting with ID ${meeting.id}.`);
    } catch (err) {
      toast.error(`Unable to copy the meeting link of meeting with ID ${meeting.id}.`);
      console.log(err);
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsDeleteCalendarEntryModalOpen = () => {
    setIsDeleteCalendarEntryModalOpen((prev) => !prev);
  };

  const onDeleteCalendarEntry = (meeting) => {
    setSelectedMeeting(meeting);
    toggleIsDeleteCalendarEntryModalOpen();
  };

  const onDeleteCalendarEntryConfirm = async () => {
    try {
      toggleIsInProgress();
      await deleteCalendarEntry(selectedMeeting.id);
      toast.success(`Deleted the calendar entry of meeting with ID ${selectedMeeting.id}.`);
      await loadCourseMeetings();
      toggleIsDeleteCalendarEntryModalOpen();
      setSelectedMeeting({});
    } catch (err) {
      toast.error(`Unable to delete the calendar entry of meeting with ID ${selectedMeeting.id}.`);
      console.log(err);
    } finally {
      toggleIsInProgress();
    }
  };

  const onCreateCalendarEntry = async (meeting) => {
    try {
      toggleIsInProgress();
      const result = await createCalendarEntry({
        crn: course.CRN,
        termcode: course.TERM_CODE,
        meetingId: meeting.id,
      });

      if (!result)
        toast.error(`Unable to create the calendar entry for meeting with ID ${meeting.id}.`);
      else {
        toast.success(`Created the calendar entry for meeting with ID ${meeting.id}.`);
        await loadCourseMeetings();
      }
    } catch (err) {
      toast.error(`Unable to create the calendar entry for meeting with ID ${meeting.id}.`);
      console.log(err);
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsAddCodecsModalOpen = () => {
    setIsAddCodecsModalOpen((prev) => !prev);
  };

  const onAddCodecs = (meeting) => {
    if (!isAddCodecsAllowed(course)) {
      toast.error(
        `The course ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN} has ended. Cannot add codecs to an ended course.`,
      );
    } else {
      setSelectedMeeting(meeting);
      toggleIsAddCodecsModalOpen();
    }
  };

  // Add Codecs
  const onAddCodecsConfirm = async (codecsData) => {
    try {
      toggleIsInProgress();
      await addCodecs({
        crn,
        termCode,
        meetingIds: [selectedMeeting.id.toString()],
        codecsData,
      });
      toast.success(`Added Codecs to the meeting with ID ${selectedMeeting.id}.`);
      toggleIsAddCodecsModalOpen();
      setSelectedMeeting({});
      await loadCourseMeetings();
    } catch (err) {
      console.log(err);
      toast.error(`Unable to add codecs to the meeting with ID ${selectedMeeting.id}.`);
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsRemoveCodecsModalOpen = () => {
    setIsRemoveCodecsModalOpen((prev) => !prev);
  };

  const onRemoveCodecs = (meeting) => {
    setSelectedMeeting(meeting);
    toggleIsRemoveCodecsModalOpen();
  };

  // Remove Codecs
  const onRemoveCodecsConfirm = async (codecsData) => {
    try {
      toggleIsInProgress();
      await removeCodecs({
        crn,
        termCode,
        meetingIds: [selectedMeeting.id.toString()],
        codecs: codecsData,
      });
      toast.success(`Removed codecs to the meeting with ID${selectedMeeting.id}.`);
      toggleIsRemoveCodecsModalOpen();
      setSelectedMeeting({});
      await loadCourseMeetings();
    } catch (err) {
      console.log(err);
      toast.error(`Unable to remove codecs to the meeting with ID ${selectedMeeting.id}.`);
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsDeleteCodecModalOpen = () => {
    setIsDeleteCodecModalOpen((prev) => !prev);
  };

  const onDeleteCodecs = (codec) => {
    setSelectedCodec(codec);
    toggleIsDeleteCodecModalOpen();
  };

  const onDeleteCodecsConfirm = async () => {
    try {
      toggleIsInProgress();
      await removeCodecs({ crn, termCode, codecs: [selectedCodec.uid] });
      toast.success(
        `Removed codec ${selectedCodec.systemName} for the course ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
      );
      await Promise.all([loadCourseDetails(), loadCourseMeetings()]);
      toggleIsDeleteCodecModalOpen();
      setSelectedCodec(null);
    } catch (err) {
      toast.error(
        `Unable to remove codec ${selectedCodec.systemName} for the course ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
      );
      console.log(err);
    } finally {
      toggleIsInProgress();
    }
  };

  const reload = async () => {
    try {
      toggleIsLoading(true);
      toggleError();
      const requests = [];
      if (error.courseDetails) {
        requests.push(loadCourseDetails());
      }

      if (error.codecs) {
        requests.push(loadCodecs());
      }

      if (error.meetings) {
        requests.push(loadCourseMeetings());
      }

      await Promise.all(requests);
    } catch (err) {
      console.log(err);
    } finally {
      toggleIsLoading(false);
    }
  };

  return (
    <>
      <Loader id={`${id}__loader`} isOpen={isLoading || isInProgress} />
      <DeleteMeetingModal
        meetingToDelete={selectedMeeting}
        toggleIsDeleteSingle={toggleIsDeleteMeetingsModalOpen}
        isDeleteSingle={isDeleteMeetingsModalOpen}
        isLoading={isInProgress}
        deleteMeeting={onDeleteMeeting}
      />
      {isAddAltHostDisplay && (
        <AddAlternativeHosts
          altHosts={alternativeHosts}
          addAltHost={onAddAlternativeHostConfirm}
          toggleIsAddAltHostDisplay={
            // Reset all the alt hosts values on close
            resetAltHostFlags
          }
          isAddAltHostDisplay={isAddAltHostDisplay}
          meeting={selectedMeeting}
          isLoading={isInProgress}
          onChangeAltHosts={setAlternativeHosts}
        />
      )}
      {isDeleteCalendarEntryModalOpen && (
        <PortalDelete
          onToggle={() => {
            toggleIsDeleteCalendarEntryModalOpen();
            setSelectedMeeting({});
          }}
          onDelete={onDeleteCalendarEntryConfirm}
          message={`Are you sure you want to delete the calendar entry for meeting with ID ${selectedMeeting.id}?`}
          title={'Delete Calendar Entry'}
        />
      )}
      {isAddCodecsModalOpen && (
        <AddCodecsModal
          isOpen={isAddCodecsModalOpen}
          toggleModalOpen={() => {
            toggleIsAddCodecsModalOpen();
            setSelectedMeeting({});
          }}
          isSmall={props.isSmall}
          onConfirm={onAddCodecsConfirm}
          id={`${id}AddCodecs`}
          codecs={Object.values(codecs)}
          course={course}
        />
      )}
      {isRemoveCodecsModalOpen && (
        <RemoveCodecsModal
          isOpen={isRemoveCodecsModalOpen}
          toggleModalOpen={() => {
            toggleIsRemoveCodecsModalOpen();
            setSelectedMeeting({});
          }}
          isSmall={props.isSmall}
          onConfirm={onRemoveCodecsConfirm}
          id={`${id}RemoveCodecs`}
          codecs={selectedMeeting.codecs.reduce((codecsList, codec) => {
            codec.data.systemName = codecs[codec.data.codecsId]?.SYSTEM_NAME;
            codecsList.push(codec.data);
            return codecsList;
          }, [])}
          course={course}
        />
      )}
      {isDeleteCodecModalOpen && (
        <PortalDelete
          onToggle={() => {
            toggleIsDeleteCodecModalOpen();
            setSelectedCodec(null);
          }}
          onDelete={onDeleteCodecsConfirm}
          message={`Are you sure you want to delete the codec ${selectedCodec.systemName}?`}
          title={'Delete Codecs'}
        />
      )}

      {!isLoading && Object.keys(error).length > 0 && (
        <Box mt={2}>
          <PortalAlert id={`${id}__portalAlert`} severity="error">
            <ApiErrorMessage
              id={`${id}__apiErrorMessage`}
              reload={reload}
              widgetName="Course Details"
              isPage={true}
            />
          </PortalAlert>
        </Box>
      )}

      {!isLoading && !Object.keys(error).length && (
        <>
          <ActionDialogs
            id={id}
            selectedCourse={course}
            setCourseActionMenu={setCourseActionMenu}
            isDetailedCourseActionsMenu={true}
            codecs={Object.values(codecs)}
            loadCourse={loadCourseDetails}
            loadMeetings={loadCourseMeetings}
            isSmall={props.isSmall}
            futureMeetings={futureMeetings}
          />
          <Box className="myOdu__cct" mt={2} id={`${id}__box_headerWrapper`}>
            <Header
              subjectCode={course.SUBJECT_CODE}
              courseNumber={course.COURSE_NUMBER}
              courseTitle={course.COURSE_TITLE}
              termCode={course.TERM_CODE}
              crns={course.CRNS}
              actionButton={
                courseActionMenu && (
                  <CourseActions
                    id={`${id}Header`}
                    courseActionMenu={courseActionMenu}
                    course={course}
                  />
                )
              }
            />
          </Box>

          <CourseInfo id={`${id}Info`} course={course} />
          <Box mt={2} id={`${id}__box_pastMeetingsWrapper`}>
            <Meetings
              title={'Past Meetings'}
              meetings={pastMeetings}
              id={`${id}PastMeetings`}
              isPastMeetings={true}
              codecs={codecs}
            />
          </Box>
          <Box mt={2} id={`${id}__box_futureMeetingsWrapper`}>
            <Meetings
              title={'Future Meetings'}
              meetings={futureMeetings}
              id={`${id}FutureMeetings`}
              crn={crn}
              termCode={termCode}
              codecs={codecs}
              onDelete={onDelete}
              onStart={onStart}
              onEnableGuestSpeaker={onEnableGuestSpeaker}
              onAddAlternativeHost={onAddAltHost}
              onEnableJoinBeforeHost={onEnableJoinBeforeHost}
              onCopyMeetingInvitation={onCopyMeetingInvitation}
              onDeleteCalendarEntry={onDeleteCalendarEntry}
              onCreateCalendarEntry={onCreateCalendarEntry}
              onAddCodecs={onAddCodecs}
              onRemoveCodecs={onRemoveCodecs}
            />
          </Box>
          <Box mt={2} id={`${id}__box_codecsWrapper`}>
            <Codecs
              title={'Codecs'}
              codecs={course.codecs ?? []}
              id={`${id}Codecs`}
              onDeleteCodecs={onDeleteCodecs}
            />
          </Box>
        </>
      )}
    </>
  );
});
